import mimeDb from 'mime-db';
import _ from 'lodash';

/**
 * Helper function to turn the mime-db data structure inside out. mime-db is a mapping from content-type
 * to extensions, but we need a mapping from extension to content-type
 * @returns Mapping from extension to content type
 */
const createMimeLookup = () => {
    let extensionMap = {};
    for (const contentType in mimeDb) {
        if (mimeDb[contentType].extensions !== undefined) {
            for (const ix in mimeDb[contentType].extensions) {
                const extension = mimeDb[contentType].extensions[ix];
                extensionMap[extension] = contentType;
            }
        }
    }
    return extensionMap;
};

/**
 * Memoize the `createMimeLookup` function so that it is only called once.
 */
const memoCreateMimeLookup = _.memoize(createMimeLookup);

/**
 * NOTE: this function is based on the documention of dropzone version 14. We are not using this version
 * right now, but I thought it would be nice to keep it for when we upgrade.
 *
 * Builds the `accept` setting for `useDropzone` based on a comma-separated list of file extensions.
 *
 * For example, if the parameter is `jpg, jpeg, png`, the return value will be:
 * {
 *   'image/jpeg': 'jpg, jpeg',
 *   'image/png': 'png'
 * }
 * @param acceptConfig A comma-separated list of file extensions.
 * @returns An object that can be passed to the `accept` parameter of `useDropzone` version 14
 */
const getAcceptV14 = (acceptConfig) => {
    if (acceptConfig === '*') return null;

    const extensions = acceptConfig.split(',').map((x) => x.trim());
    const mimeLookup = memoCreateMimeLookup();

    return extensions.reduce((acc, ext) => {
        const extNoDot = _.trimStart(ext, '.');
        const extWithDot = '.' + extNoDot;

        const mimeType = _.get(mimeLookup, extNoDot, 'application/octet-stream');

        if (acc[mimeType]) {
            return {
                ...acc,
                [mimeType]: [...acc[mimeType], extWithDot],
            };
        }

        return {
            ...acc,
            [mimeType]: [extWithDot],
        };
    }, {});
};

/**
 * NOTE: This function is based on the documention of dropzone version 11, which is currently used.
 *
 * Builds the `accept` setting for `useDropzone` based on a comma-separated list of file extensions.
 *
 * For example, if the parameter is `jpg, jpeg, png`, the return value will be:
 * ['image/jpeg', 'image/png']
 * @param acceptConfig A comma-separated list of extensions.
 * @returns An array of mime types that matches the given extensions
 */
const getAcceptV11 = (acceptConfig) => {
    if (acceptConfig === '*') return null;

    const extensions = acceptConfig.split(',').map((x) => x.trim());
    const mimeLookup = memoCreateMimeLookup();

    return extensions.reduce((acc, ext) => {
        const extNoDot = _.trimStart(ext, '.');

        const mimeType = _.get(mimeLookup, extNoDot, 'application/octet-stream');

        if (_.includes(acc, mimeType)) return acc;
        return [...acc, mimeType];
    }, []);
};

export {getAcceptV11, getAcceptV14};
